// Shared timestamp display preference.
//
// All trade/snapshot times are stored as UTC ISO strings. By default we show
// them in the VISITOR'S local timezone (auto-detected by the browser) — that's
// what every exchange does and what a depositor expects ("21:32" in their own
// evening, not a UTC figure they have to mentally convert). A persisted toggle
// flips to UTC for the minority who cross-reference the on-chain HL / Hyperscan
// explorers, which report in UTC.
//
// Usage at a call site (keep the site's own locale + format options):
//   import { tzZone, tzLabel } from '../time.js';
//   d.toLocaleTimeString('en-US', { ...opts, timeZone: tzZone() }) + ' ' + tzLabel(d)
//
// tzZone() returns 'UTC' in UTC mode, or undefined in local mode (passing
// undefined to Intl uses the runtime's local zone — exactly what we want).

const TZ_KEY = 'cc_tz_mode';   // 'local' | 'utc'

export function getTzMode() {
  return localStorage.getItem(TZ_KEY) === 'utc' ? 'utc' : 'local';
}

export function setTzMode(mode) {
  localStorage.setItem(TZ_KEY, mode === 'utc' ? 'utc' : 'local');
}

// The IANA zone to format in: 'UTC' when forced, else undefined (= local zone).
export function tzZone() {
  return getTzMode() === 'utc' ? 'UTC' : undefined;
}

// One Intl-formatted timeZoneName part, e.g. 'short' → "EST"/"GMT+2",
// 'long' → "Central European Summer Time".
function _tzPart(d, style) {
  const parts = new Intl.DateTimeFormat('en-US', { timeZoneName: style }).formatToParts(d);
  const p = parts.find(x => x.type === 'timeZoneName');
  return p ? p.value : '';
}

// Short tz label for the active mode, DST-aware for the visitor's zone.
// Prefers a named abbreviation (US zones give "EST"/"PDT" directly; European
// zones don't — the browser returns "GMT+2", so we build the acronym from the
// long name: "Central European Summer Time" → "CEST"). Falls back to the GMT+N
// offset when no clean abbreviation can be derived.
export function tzLabel(d = new Date()) {
  if (getTzMode() === 'utc') return 'UTC';
  try {
    const short = _tzPart(d, 'short');                 // "EST" | "GMT+2" | ""
    // Real letter-only abbreviation (not a GMT/UTC offset) → use as-is.
    if (short && !/^(GMT|UTC)/.test(short) && /^[A-Za-z]+$/.test(short)) return short;
    // Numeric-offset zone → initials of the long name ("CEST", "GMT", "IST").
    const long = _tzPart(d, 'long');                   // "Central European Summer Time"
    const acro = long.split(/\s+/).filter(w => /^[A-Z]/.test(w)).map(w => w[0]).join('');
    if (acro.length >= 2 && acro.length <= 5) return acro;
    return short || 'Local';                           // fallback: "GMT+2"
  } catch {
    return 'Local';
  }
}
